<script lang="ts" setup>
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import { DesiredWorkSchedule } from "@/types/desired_work_schedule/DesiredWorkSchedule";
import {
  addMonths,
  calcDate,
  endOfMonth,
  formatFromISOString,
  getDate,
  getDay,
  getMonthIndex,
  getYear,
  now,
  parseISO,
  subDays
} from "@/utils/date";
import { computed, ref } from "vue";

// カレンダー日付の型定義
interface CalendarDay {
  day: number; // 日付の数値部分（1-31）
  date: string; // YYYY-MM-DD形式の文字列
  isCurrentMonth: boolean; // 現在表示中の月かどうか
  isToday: boolean; // 今日の日付かどうか
  isHoliday: boolean; // 祝日かどうか
  desiredWorkSchedule?: DesiredWorkSchedule; // 「働きたい日」情報
}

const props = defineProps({
  desiredWorkSchedules: {
    type: Array as () => DesiredWorkSchedule[],
    default: () => []
  },
  holidays: {
    type: Array as () => string[],
    default: () => []
  }
});

const emit = defineEmits(["dayClick"]);

const nowValue = now();

// 現在表示中の年月
const currentYear = ref(getYear(nowValue));
const currentMonth = ref(getMonthIndex(nowValue) + 1);
const selectedDate = ref<string | null>(null);

// 表示可能な最小・最大日付を計算（前後2ヶ月まで）
const minDate = computed(() => {
  const d = addMonths(nowValue, -2);
  const year = getYear(d);
  const month = getMonthIndex(d) + 1;
  return { year, month };
});
const maxDate = computed(() => {
  const d = addMonths(nowValue, 2);
  const year = getYear(d);
  const month = getMonthIndex(d) + 1;
  return { year, month };
});

const isPrevMonthDisabled = computed(() => {
  const currentYearMonth = formatYearMonth(
    currentYear.value,
    currentMonth.value
  );
  const minYearMonth = formatYearMonth(minDate.value.year, minDate.value.month);
  return currentYearMonth <= minYearMonth;
});

const isNextMonthDisabled = computed(() => {
  const currentYearMonth = formatYearMonth(
    currentYear.value,
    currentMonth.value
  );
  const maxYearMonth = formatYearMonth(maxDate.value.year, maxDate.value.month);
  return currentYearMonth >= maxYearMonth;
});

// 月の最初と最後の日を計算
const firstDayOfMonth = computed(() => {
  return calcDate(currentYear.value, currentMonth.value, 1);
});
const lastDayOfMonth = computed(() => {
  return endOfMonth(calcDate(currentYear.value, currentMonth.value, 1));
});

// カレンダーの日付配列を生成
const calendarDays = computed((): CalendarDay[] => {
  const days: CalendarDay[] = [];

  // 月の最初の日の曜日を取得（1: 月曜日, 2: 火曜日, ...）
  const firstDayOfWeek = getDay(firstDayOfMonth.value);

  // 前月の日を追加
  const prevMonthLastDate = getDate(subDays(firstDayOfMonth.value, 1));

  // 前月の年と月を計算
  const prevMonthYear =
    currentMonth.value === 1 ? currentYear.value - 1 : currentYear.value;
  const prevMonth = currentMonth.value === 1 ? 12 : currentMonth.value - 1;

  // 初週が前月の日付で埋まるようであれば前月の日付の追加はスキップし、カレンダーの初週が空になることを防ぐ
  if (firstDayOfWeek % 7 !== 0) {
    for (let i = 0; i < firstDayOfWeek; i++) {
      const dayNumber = prevMonthLastDate - firstDayOfWeek + i + 1;
      const date = formatDateString(prevMonthYear, prevMonth, dayNumber);

      days.push({
        day: dayNumber,
        date,
        isCurrentMonth: false,
        isToday: false,
        isHoliday: isHoliday(date)
      });
    }
  }

  // 今月の日を追加
  const lastDate = getDate(lastDayOfMonth.value);
  const isCurrentMonthToday =
    getYear(nowValue) === currentYear.value &&
    getMonthIndex(nowValue) === currentMonth.value - 1;

  for (let i = 1; i <= lastDate; i++) {
    const date = formatDateString(currentYear.value, currentMonth.value, i);
    const desiredWorkSchedule = props.desiredWorkSchedules.find(
      schedule => schedule.date === date
    );

    days.push({
      day: i,
      date,
      isCurrentMonth: true,
      isToday: isCurrentMonthToday && getDate(nowValue) === i,
      isHoliday: isHoliday(date),
      desiredWorkSchedule
    });
  }

  // 次月の年と月を計算
  const nextMonthYear =
    currentMonth.value === 12 ? currentYear.value + 1 : currentYear.value;
  const nextMonth = currentMonth.value === 12 ? 1 : currentMonth.value + 1;

  // 次月の日を追加（6週間表示するために必要な場合）
  const daysNeeded = 42 - days.length; // 6週間分（42日）
  for (let i = 1; i <= daysNeeded; i++) {
    const date = formatDateString(nextMonthYear, nextMonth, i);

    days.push({
      day: i,
      date,
      isCurrentMonth: false,
      isToday: false,
      isHoliday: isHoliday(date)
    });
  }

  return days;
});

// 週ごとに分割した日付配列
const calendarWeeks = computed((): CalendarDay[][] => {
  const weeks: CalendarDay[][] = [];
  for (let i = 0; i < calendarDays.value.length; i += 7) {
    weeks.push(calendarDays.value.slice(i, i + 7));
  }
  return weeks;
});

const formatYearMonth = (year: number, month: number): string => {
  return `${year}${String(month).padStart(2, "0")}`;
};

const formatDateString = (year: number, month: number, day: number): string => {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
    2,
    "0"
  )}`;
};

// 祝日かどうかをチェックするヘルパー関数
const isHoliday = (date: string): boolean => {
  return props.holidays.includes(date);
};

// 前月へ移動（制限範囲内のみ）
const goToPrevMonth = () => {
  if (isPrevMonthDisabled.value) return; // 範囲外なら何もしない

  if (currentMonth.value === 1) {
    currentYear.value--;
    currentMonth.value = 12;
  } else {
    currentMonth.value--;
  }
};

// 次月へ移動（制限範囲内のみ）
const goToNextMonth = () => {
  if (isNextMonthDisabled.value) return; // 範囲外なら何もしない

  if (currentMonth.value === 12) {
    currentYear.value++;
    currentMonth.value = 1;
  } else {
    currentMonth.value++;
  }
};

// 日付クリックハンドラを修正
const handleDayClick = (day: CalendarDay) => {
  selectedDate.value = day.date;

  const todayString = formatDateString(
    getYear(nowValue),
    getMonthIndex(nowValue) + 1,
    getDate(nowValue)
  );
  if (day.isCurrentMonth && day.date >= todayString) {
    emit("dayClick", day.date);
  }
};

// 日付が現在選択されているかを確認するヘルパー関数
const isSelectedDate = (day: CalendarDay): boolean => {
  if (!selectedDate.value || !day.isCurrentMonth) return false;
  return day.date === selectedDate.value;
};

// 時間表示用のヘルパー関数
const formatBeginTime = (schedule: DesiredWorkSchedule): string => {
  if (schedule.isAllDay) {
    return "終日";
  }

  return formatFromISOString(schedule.startAt!, "HH:mm");
};

const formatEndTime = (schedule: DesiredWorkSchedule): string => {
  if (schedule.isAllDay) {
    return "";
  }

  const finishTimeStr = formatFromISOString(schedule.finishAt!, "HH:mm");

  const startTime = parseISO(schedule.startAt!);
  const finishTime = parseISO(schedule.finishAt!);

  // 終了時間と開始時間が同じ日付でない場合、翌日の時間として表示する
  if (getDay(finishTime) !== getDay(startTime)) {
    return `翌${finishTimeStr}`;
  }

  return finishTimeStr;
};
</script>

<template>
  <div class="calendar" data-testid="calendar">
    <div class="calendar-header" data-testid="calendar-header">
      <button
        @click="goToPrevMonth"
        class="month-nav"
        :disabled="isPrevMonthDisabled"
        :class="{ disabled: isPrevMonthDisabled }"
        data-testid="prev-month-button"
      >
        <FeatherIcon icon="chevron-left" size="24" />
      </button>
      <h3 data-testid="current-month-year">
        {{ currentYear }}年{{ currentMonth }}月
      </h3>
      <button
        @click="goToNextMonth"
        class="month-nav"
        :disabled="isNextMonthDisabled"
        :class="{ disabled: isNextMonthDisabled }"
        data-testid="next-month-button"
      >
        <FeatherIcon icon="chevron-right" size="24" />
      </button>
    </div>

    <div class="calendar-weekdays" data-testid="calendar-weekdays">
      <div
        v-for="(day, index) in ['日', '月', '火', '水', '木', '金', '土']"
        :key="'weekday-' + index"
        class="weekday"
        :class="{ sunday: index === 0, saturday: index === 6 }"
        :data-testid="`weekday-${index}`"
      >
        {{ day }}
      </div>
    </div>

    <div class="calendar-body" data-testid="calendar-body">
      <div
        v-for="(week, weekIndex) in calendarWeeks"
        :key="'week-' + weekIndex"
        class="calendar-week"
        :data-testid="`calendar-week-${weekIndex}`"
      >
        <div
          v-for="(day, dayIndex) in week"
          :key="'day-' + weekIndex + '-' + dayIndex"
          class="calendar-day"
          :class="{
            'other-month': !day.isCurrentMonth,
            today: day.isToday,
            'desired-work-day': day.desiredWorkSchedule && day.isCurrentMonth,
            sunday: dayIndex === 0,
            saturday: dayIndex === 6,
            holiday: day.isHoliday, // 祝日クラスを追加
            selected: isSelectedDate(day)
          }"
          @click="handleDayClick(day)"
          :data-testid="`calendar-day-${day.date}`"
        >
          <span class="date-number" data-testid="date-number">{{
            day.isCurrentMonth ? day.day : ""
          }}</span>

          <div
            v-if="day.desiredWorkSchedule && day.isCurrentMonth"
            class="work-time"
            data-testid="work-time"
          >
            <div data-testid="work-begin-time">
              {{ formatBeginTime(day.desiredWorkSchedule) }}
            </div>
            <div
              v-if="!day.desiredWorkSchedule.isAllDay"
              data-testid="work-end-time"
            >
              {{ formatEndTime(day.desiredWorkSchedule) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.calendar {
  width: 100%;
  margin-bottom: 20px;
  overflow: hidden;
  background-color: #fff;
  border-top: 1px solid #e5e5e5;
  border-right: 1px solid #e5e5e5;
  border-left: 1px solid #e5e5e5;
  border-radius: 8px;
}

.calendar-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background-color: #fff;

  h3 {
    margin: 0 8px;
    font-size: 16px;
    font-weight: normal;
  }

  .month-nav {
    padding: 4px 8px;
    color: #3f88ae;
    cursor: pointer;
    background: none;
    border: none;
    border-radius: 4px;

    &:hover {
      background-color: #e9ecef;
    }

    &.disabled {
      cursor: not-allowed;
      opacity: 0.4;

      &:hover {
        background-color: transparent;
      }
    }
  }
}

.calendar-weekdays {
  display: flex;
  background-color: #fafafa;
  border-top: 1px solid #e5e5e5;
  border-bottom: 1px solid #e5e5e5;

  .weekday {
    flex: 1;
    padding: 8px 0;
    font-size: 14px;
    font-weight: normal;
    text-align: center;
  }

  .sunday {
    color: #c2493e;
  }

  .saturday {
    color: #3f88ae;
  }
}

.calendar-body {
  .calendar-week {
    display: flex;
  }

  .calendar-day {
    position: relative;
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 60px;
    cursor: pointer;
    border-right: 1px solid #e5e5e5;
    border-bottom: 1px solid #e5e5e5;

    &:last-child {
      border-right: none;
    }

    .date-number {
      position: absolute;
      top: 3px;
      left: 5px;
      font-size: 12px;
      line-height: 1;
      color: #666;
    }

    .work-time {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      margin-top: 12px; // 上部にあるdateとの間を少し空ける
      font-size: 11px;
      line-height: 1.4;
      color: #000;
      text-align: center;
    }

    &.selected {
      position: relative;
      z-index: 1;
      outline: 2px solid #fbce5a;
      outline-offset: -2px;
    }
  }

  .other-month {
    color: #adb5bd;

    .date-number {
      color: #d5d9dd;
    }
  }

  .today {
    font-weight: bold;
    background-color: #e7f5ff;

    .date-number {
      font-weight: bold;
    }
  }

  .desired-work-day {
    background-color: #ffefc780;

    &.today {
      background-color: #ffecb3;
    }
  }

  .sunday {
    color: #c2493e;

    .date-number {
      color: #c2493e;
    }
  }

  .saturday {
    color: #3f88ae;

    .date-number {
      color: #3f88ae;
    }
  }

  /* 祝日のスタイルを追加（日曜日と同じ赤色にする） */
  .holiday {
    color: #c2493e;

    .date-number {
      color: #c2493e;
    }
  }
}
</style>
